import * as yup from "yup";

import { nameSchema, phoneSchema, requiredStringSchema } from "./commonSchemas";

// Define gender and marital status options
export const genderOptions = [
  "Male",
  "Female",
  "Other",
  "Prefer not to say",
] as const;

export const maritalStatusOptions = [
  "Single",
  "Married",
  "Divorced",
  "Widowed",
  "Separated",
  "Other",
] as const;

export const contactMethodOptions = ["Phone", "Email", "SMS"] as const;

export const relationshipOptions = [
  "Spouse",
  "Parent",
  "Child",
  "Sibling",
  "Friend",
  "Other",
] as const;

export const bloodTypeOptions = [
  "A+",
  "A-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "O+",
  "O-",
] as const;

export const familyMedicalHistoryOptions = [
  "Diabetes",
  "Hypertension",
  "Asthma",
  "Cancer",
  "Heart Disease",
  "Mental Health Conditions",
] as const;

export type GenderType = (typeof genderOptions)[number];
export type MaritalStatusType = (typeof maritalStatusOptions)[number];
export type ContactMethodType = (typeof contactMethodOptions)[number];
export type RelationshipType = (typeof relationshipOptions)[number];
export type BloodTypeType = (typeof bloodTypeOptions)[number];
export type FamilyMedicalHistoryType =
  (typeof familyMedicalHistoryOptions)[number];

export const patientSchema = yup.object().shape({
  firstName: nameSchema,
  middleName: yup.string().optional(),
  lastName: nameSchema,
  dateOfBirth: yup.date().nullable().required("Date of birth is required"),
  gender: yup
    .string()
    .oneOf(genderOptions, "Please select a valid gender option")
    .required("Gender is required"),
  maritalStatus: yup
    .string()
    .oneOf(maritalStatusOptions, "Please select a valid marital status")
    .required("Marital status is required"),
  address: requiredStringSchema("Address"),
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  phoneNumber: phoneSchema,
  alternativePhone: yup.string().optional(),
  preferredContactMethod: yup
    .string()
    .oneOf(contactMethodOptions, "Please select a valid contact method")
    .required("Preferred contact method is required"),
  profileImage: yup.mixed<File>().optional().nullable(),

  // Emergency Contact
  emergencyContactName: nameSchema,
  emergencyContactRelationship: yup
    .string()
    .oneOf(relationshipOptions, "Please select a valid relationship")
    .required("Relationship is required"),
  emergencyContactPhone: phoneSchema,
  emergencyContactEmail: yup
    .string()
    .email("Please enter a valid email address")
    .optional(),

  // Medication Information
  bloodType: yup
    .string()
    .oneOf(bloodTypeOptions, "Please select a valid blood type")
    .required("Blood type is required"),
  height: yup
    .number()
    .positive("Height must be a positive number")
    .required("Height is required"),
  weight: yup
    .number()
    .positive("Weight must be a positive number")
    .required("Weight is required"),
  allergies: yup.string().required("Allergies are required"),
  currentMedicine: yup.string().required("Current medicine is required"),
  chronicConditions: yup.string().required("Chronic conditions are required"),

  // Medication History
  pastSurgeries: yup.string().optional(),
  pastHospitalizations: yup.string().optional(),
  familyMedicalHistory: yup
    .array()
    .of(yup.string().required())
    .required("Family medical history is required"),

  // Insurance & Billing Information
  insuranceNumber: requiredStringSchema("Insurance number"),
  insuranceProvider: requiredStringSchema("Insurance provider"),

  // Consent & Documents
  hipaaConsentForm: yup
    .mixed<File>()
    .required("HIPAA consent form is required"),
  treatmentConsent: yup
    .mixed<File>()
    .required("Treatment consent form is required"),
  financialAgreement: yup
    .mixed<File>()
    .required("Financial agreement form is required"),
});
